import React, { useState, useEffect } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

// Define types for our component
interface JsonToSqlProps {}

// SQL dialect options
type SqlDialect = 'mysql' | 'postgresql' | 'sqlite' | 'sqlserver';

const JsonToSql: React.FC<JsonToSqlProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the SQL output
  const [sqlOutput, setSqlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the table name
  const [tableName, setTableName] = useState<string>('my_table');
  // State for the selected SQL dialect
  const [sqlDialect, setSqlDialect] = useState<SqlDialect>('mysql');
  // State for copy button
  const [copied, setCopied] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for include column names
  const [includeColumnNames, setIncludeColumnNames] = useState<boolean>(true);
  // State for batch size (0 means no batching)
  const [batchSize, setBatchSize] = useState<number>(0);
  // State to track if the conversion happened
  const [hasConverted, setHasConverted] = useState<boolean>(false);

  // Helper function to get the SQL quote character based on dialect
  const getQuoteChar = (dialect: SqlDialect): string => {
    switch (dialect) {
      case 'mysql':
        return '`';
      case 'postgresql':
      case 'sqlite':
        return '"';
      case 'sqlserver':
        return '[';
      default:
        return '`';
    }
  };

  // Helper function to get the closing quote character based on dialect
  const getCloseQuoteChar = (dialect: SqlDialect): string => {
    return dialect === 'sqlserver' ? ']' : getQuoteChar(dialect);
  };

  // Helper function to format a value for SQL based on its type
  const formatSqlValue = (value: any, dialect: SqlDialect): string => {
    if (value === null || value === undefined) {
      return 'NULL';
    }

    if (typeof value === 'string') {
      // Escape single quotes by doubling them
      const escapedValue = value.replace(/'/g, "''");
      return `'${escapedValue}'`;
    }

    if (typeof value === 'boolean') {
      switch (dialect) {
        case 'mysql':
        case 'postgresql':
          return value ? 'TRUE' : 'FALSE';
        case 'sqlite':
          return value ? '1' : '0';
        case 'sqlserver':
          return value ? '1' : '0';
        default:
          return value ? 'TRUE' : 'FALSE';
      }
    }

    if (value instanceof Date) {
      return `'${value.toISOString()}'`;
    }

    if (typeof value === 'object') {
      try {
        return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
      } catch (e) {
        return 'NULL';
      }
    }

    return String(value);
  };

  // Convert JSON to SQL based on the selected options
  const convertJsonToSql = () => {
    setError('');
    setSqlOutput('');
    setHasConverted(false);

    if (!jsonInput.trim()) {
      setError('Please enter some JSON data.');
      return;
    }

    try {
      // Parse the JSON input
      let jsonData = JSON.parse(jsonInput);

      // If it's not an array, wrap it in an array
      if (!Array.isArray(jsonData)) {
        jsonData = [jsonData];
      }

      // Check if there's at least one item
      if (jsonData.length === 0) {
        setError('No data found in the JSON.');
        return;
      }

      // Get all unique keys from all objects to build columns
      const allColumns = new Set<string>();
      jsonData.forEach((item: any) => {
        Object.keys(item).forEach(key => allColumns.add(key));
      });

      const columns = Array.from(allColumns);
      const quoteChar = getQuoteChar(sqlDialect);
      const closeQuoteChar = getCloseQuoteChar(sqlDialect);

      // Generate SQL statements
      let sql = '';
      
      // Split into batches if batch size is greater than 0
      if (batchSize > 0) {
        for (let i = 0; i < jsonData.length; i += batchSize) {
          const batchData = jsonData.slice(i, i + batchSize);
          sql += generateInsertStatement(batchData, columns, quoteChar, closeQuoteChar);
          sql += '\n\n';
        }
      } else {
        // No batching
        sql = generateInsertStatement(jsonData, columns, quoteChar, closeQuoteChar);
      }

      setSqlOutput(sql);
      setHasConverted(true);
    } catch (err) {
      setError(`Failed to parse JSON: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Generate INSERT statement for a batch of data
  const generateInsertStatement = (
    data: any[], 
    columns: string[], 
    quoteChar: string, 
    closeQuoteChar: string
  ): string => {
    let sql = `INSERT INTO ${quoteChar}${tableName}${closeQuoteChar} `;

    // Add column names if option is selected
    if (includeColumnNames) {
      sql += `(${columns.map(col => `${quoteChar}${col}${closeQuoteChar}`).join(', ')}) `;
    }

    sql += 'VALUES\n';

    // Add values for each row
    const rows = data.map(item => {
      const values = columns.map(column => {
        return formatSqlValue(item[column], sqlDialect);
      });
      return `(${values.join(', ')})`;
    });

    sql += rows.join(',\n');
    sql += ';';

    return sql;
  };

  // Handle clipboard copy
  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    // Clear any previous output or errors
    setSqlOutput('');
    setError('');
    setHasConverted(false);
  };

  // Clear all inputs and outputs
  const clearAll = () => {
    setJsonInput('');
    setSqlOutput('');
    setError('');
    setTableName('my_table');
    setSqlDialect('mysql');
    setIncludeColumnNames(true);
    setBatchSize(0);
    setHasConverted(false);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to SQL Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to SQL INSERT statements. Paste your JSON array or object to generate SQL code for your database.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setHasConverted(false);
              }}
              placeholder="Paste your JSON data here..."
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="tableName" className="block text-sm font-medium text-gray-700 mb-1">
                Table Name
              </label>
              <input
                type="text"
                id="tableName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="e.g., users, products, etc."
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="sqlDialect" className="block text-sm font-medium text-gray-700 mb-1">
                SQL Dialect
              </label>
              <select
                id="sqlDialect"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={sqlDialect}
                onChange={(e) => setSqlDialect(e.target.value as SqlDialect)}
              >
                <option value="mysql">MySQL</option>
                <option value="postgresql">PostgreSQL</option>
                <option value="sqlite">SQLite</option>
                <option value="sqlserver">SQL Server</option>
              </select>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeColumnNames"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={includeColumnNames}
                    onChange={(e) => setIncludeColumnNames(e.target.checked)}
                  />
                  <label htmlFor="includeColumnNames" className="ml-2 block text-sm text-gray-700">
                    Include Column Names
                  </label>
                </div>
                
                <div>
                  <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
                    Batch Size (0 for no batching)
                  </label>
                  <input
                    type="number"
                    id="batchSize"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={batchSize}
                    onChange={(e) => setBatchSize(Math.max(0, parseInt(e.target.value) || 0))}
                    min="0"
                    placeholder="Number of rows per INSERT statement"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For large datasets, batching can improve performance.
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToSql}
              >
                Convert to SQL
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label htmlFor="sqlOutput" className="font-medium text-gray-700">
              SQL Output
            </label>
            
            {sqlOutput && (
              <CopyToClipboard text={sqlOutput} onCopy={handleCopy}>
                <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy SQL'}
                </button>
              </CopyToClipboard>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !sqlOutput && !hasConverted && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <p className="text-center">
                  SQL output will appear here after conversion.
                </p>
              </div>
            )}
            
            {!error && !sqlOutput && hasConverted && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-3 rounded-md">
                <p>No SQL was generated. Please check your JSON data and try again.</p>
              </div>
            )}
            
            {!error && sqlOutput && (
              <textarea
                id="sqlOutput"
                className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                value={sqlOutput}
                readOnly
              />
            )}
          </div>
          
          {hasConverted && sqlOutput && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <p>✅ SQL successfully generated!</p>
              {batchSize > 0 && (
                <p className="text-sm mt-1">
                  Data has been split into batches of {batchSize} rows per INSERT statement.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to SQL Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool takes a JSON array (or a single object) and converts it into SQL INSERT statements. 
              It automatically extracts all column names from the JSON objects and formats the values 
              according to the selected SQL dialect.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Supported SQL Dialects</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>MySQL</strong> - Uses backticks (`) for identifiers and the TRUE/FALSE keywords for boolean values</li>
              <li><strong>PostgreSQL</strong> - Uses double quotes (") for identifiers and the TRUE/FALSE keywords for boolean values</li>
              <li><strong>SQLite</strong> - Uses double quotes (") for identifiers and 1/0 for boolean values</li>
              <li><strong>SQL Server</strong> - Uses square brackets ([]) for identifiers and 1/0 for boolean values</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Ensure your JSON is valid before conversion</li>
              <li>For large datasets, use batching to split the output into multiple INSERT statements</li>
              <li>Complex nested objects will be converted to JSON strings in the SQL output</li>
              <li>Date strings will be preserved as-is; format them according to your database requirements before conversion</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToSql;